import { useCallback } from 'react';
import { GetRolesLite, RoleLiteItem } from '@/apis/roles';
import {
  useLiteDropdown,
  UseLiteDropdownOptions,
} from '@/common/helper/useLiteDropdown';
import {
  PaginatedRequest,
  PaginatedResponse,
} from '@/common/helper/usePaginatedDropdown';

export interface UseRoleLiteDropdownParams extends Partial<
  Pick<UseLiteDropdownOptions<RoleLiteItem>, 'initialLimit' | 'initialSearch'>
> {
  unauthorize?: (error: unknown) => void;
}

/**
 * Domain hook for the roles lite dropdown.
 *
 * Calls GET /api/auth/roles/?lite=true with pagination and search.
 * Delegates scroll handling and pagination state to useLiteDropdown.
 */
export const useRoleLiteDropdown = ({
  initialLimit = 20,
  initialSearch = '',
  unauthorize,
}: UseRoleLiteDropdownParams = {}) => {
  const fetchFn = useCallback(
    async ({
      page,
      limit,
      search,
    }: PaginatedRequest): Promise<PaginatedResponse<RoleLiteItem>> => {
      try {
        const response = await GetRolesLite(page, search || undefined, limit);
        const resData = response?.data?.data;
        const items = Array.isArray(resData?.data) ? resData.data : [];
        const total = resData?.total_count ?? items.length;
        const pageCount = resData?.page_count ?? 1;
        return {
          data: items,
          total,
          pageCount,
          hasNext: page < pageCount,
        };
      } catch (error) {
        unauthorize?.(error);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [unauthorize]
  );

  const lite = useLiteDropdown<RoleLiteItem>({
    fetchFn,
    initialLimit,
    initialSearch,
  });

  return {
    /** Role items for the current loaded pages */
    roleList: lite.data,
    roles: lite.data,
    hasMore: lite.hasMore,
    loading: lite.loading,
    search: lite.search,
    setSearch: lite.setSearch,
    loadMore: lite.loadMore,
    handlePopupScroll: lite.handlePopupScroll,
    fetchAllMatching: lite.fetchAllMatching,
  };
};

export default useRoleLiteDropdown;
